import * as Network from 'expo-network';
import { Dynamo } from '../primary-record-types';
import { useEffect, useState } from 'react';

async function getIpLocation(ip: string | null): Promise<Dynamo.IPLocation | null> {
    if (!ip) return null;

    try {
        const url = `https://ipapi.co/${ip}/json/`;
        const response = await fetch(url, {
            headers: {
                'User-Agent': 'nodejs-ipapi-v1.02',
            },
        });
        const data = (await response.json()) as Dynamo.IPLocation;
        if (!data?.ip) {
            return null;
        }

        return data;
    } catch (error) {
        return null;
    }
}

export async function getDeviceIpLocation(): Promise<Dynamo.IPLocation | null> {
    const ip = (await Network.getIpAddressAsync()) || null;
    return getIpLocation(ip);
}

type PendingIpStatus = {
    status: 'pending';
};

type SuccessIpStatus = {
    status: 'success';
    location: Dynamo.IPLocation;
};

type ErrorIpStatus = {
    status: 'error';
};

let ipStatus: PendingIpStatus | SuccessIpStatus | ErrorIpStatus = { status: 'pending' };

export function useLocation(): PendingIpStatus | SuccessIpStatus | ErrorIpStatus {
    const [ipStatusState, setIpStatusState] = useState<PendingIpStatus | SuccessIpStatus | ErrorIpStatus>(ipStatus);
    useEffect(() => {
        getDeviceIpLocation()
            .then((location) => {
                if (location) {
                    ipStatus = { status: 'success', location };
                } else {
                    ipStatus = { status: 'error' };
                }
                setIpStatusState(ipStatus);
            })
            .catch((e) => {
                console.error('Error getting location', e);
                ipStatus = { status: 'error' };
                setIpStatusState(ipStatus);
            });
    }, []);

    return ipStatusState;
}

export type USStates =
    | 'Alabama'
    | 'Alaska'
    | 'Arizona'
    | 'Arkansas'
    | 'California'
    | 'Colorado'
    | 'Connecticut'
    | 'Delaware'
    | 'Florida'
    | 'Georgia'
    | 'Hawaii'
    | 'Idaho'
    | 'Illinois'
    | 'Indiana'
    | 'Iowa'
    | 'Kansas'
    | 'Kentucky'
    | 'Louisiana'
    | 'Maine'
    | 'Maryland'
    | 'Massachusetts'
    | 'Michigan'
    | 'Minnesota'
    | 'Mississippi'
    | 'Missouri'
    | 'Montana'
    | 'Nebraska'
    | 'Nevada'
    | 'New Hampshire'
    | 'New Jersey'
    | 'New Mexico'
    | 'New York'
    | 'North Carolina'
    | 'North Dakota'
    | 'Ohio'
    | 'Oklahoma'
    | 'Oregon'
    | 'Pennsylvania'
    | 'Rhode Island'
    | 'South Carolina'
    | 'South Dakota'
    | 'Tennessee'
    | 'Texas'
    | 'Utah'
    | 'Vermont'
    | 'Virginia'
    | 'Washington'
    | 'Washington D.C.'
    | 'West Virginia'
    | 'Wisconsin'
    | 'Wyoming';

const singlePartyConsentStates: USStates[] = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'Colorado',
    'Delaware',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Nebraska',
    'Nevada',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
    'Washington D.C.',
];

const allPartyConsentStates: USStates[] = [
    'California',
    'Connecticut',
    'Florida',
    'Illinois',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Montana',
    'New Hampshire',
    'Oregon',
    'Pennsylvania',
    'Washington',
];

export type LoadingPartyConsentValue = {
    status: 'loading';
};

export type UnknownPartyConsentValue = {
    status: 'unknown';
};

export type SuccessPartyConsentValue = {
    status: 'success';
    consent: 'single' | 'all';
    state: USStates;
};

type UseLocationPartyConsentStatusReturnValue =
    | LoadingPartyConsentValue
    | UnknownPartyConsentValue
    | SuccessPartyConsentValue;

export const useLocationPartyConsentStatus = (): UseLocationPartyConsentStatusReturnValue => {
    const location = useLocation();
    if (location.status === 'pending') {
        return { status: 'loading' };
    } else if (location.status === 'error') {
        return { status: 'unknown' };
    } else {
        const state = location.location.region as USStates;
        if (singlePartyConsentStates.includes(state)) {
            return { status: 'success', consent: 'single', state };
        } else if (allPartyConsentStates.includes(state)) {
            return { status: 'success', consent: 'all', state };
        } else {
            return { status: 'unknown' };
        }
    }
};
